import React, { useState } from "react";
import { INK, MUTED, W, M, axis, scale, fmt, count, plural, sentenceList, clusterColor, Halo, Legend, Tooltip, Axes } from "./clusterKit";

// ---- dendrogram: the last merges of a hierarchical clustering, with the cut that gives k clusters ----
// Props (ChartData#dendrogram): leaves [{ x, size, height, cluster }] (each a branch of earlier merges),
// links [{ x, height, left [x, height], right [x, height], size, cluster }] (one per merge drawn; cluster null above the cut),
// n, k, cutHeight, heightBelow, heightAbove (the merges either side of the cut), cut [{ cluster, size, moved }],
// moved, clusterNames, finalSizes, legendTitle, noun.
export default function DendrogramChart({ leaves, links, n, k, cutHeight, heightBelow, heightAbove, cut, moved, clusterNames, finalSizes, legendTitle, noun = "participant" }) {
	const [hover, setHover] = useState(null);
	const height = 360, box = { l: 44, r: W - M.right, t: M.top + 22, b: height - 88 };
	const top = Math.max(...links.map((l) => l.height));
	const axY = axis([0, top], 0.04);
	const x = scale({ min: -0.5, max: leaves.length - 0.5 }, box.l, box.r), y = scale(axY, box.b, box.t);
	const path = (l) => `M${x(l.left[0])},${y(l.left[1])} V${y(l.height)} H${x(l.right[0])} V${y(l.right[1])}`;
	const tops = [...links].sort((a, b) => b.height - a.height).slice(0, 3).map((l) => fmt(l.height)).reverse();
	// Each cut cluster is named under its leaves, which sit side by side (a branch's leaves always do).
	const brackets = cut.map((c) => {
		const xs = leaves.filter((lf) => lf.cluster === c.cluster).map((lf) => lf.x);
		return { ...c, x1: x(Math.min(...xs)) - 8, x2: x(Math.max(...xs)) + 8 };
	});
	const sizes = cut.map((c) => count(c.size));
	return (
		<figure>
			<Legend title={legendTitle} items={[...cut.map((c) => ({ label: clusterNames[c.cluster], color: clusterColor(c.cluster), kind: "line" })),
				{ label: "Above the cut", color: MUTED, kind: "line" }, { label: `Cut: ${k} clusters`, color: INK, kind: "dash" }]} />
			<svg viewBox={`0 0 ${W} ${height}`} className="mt-2 w-full" role="img"
				aria-label={`Dendrogram of the last ${links.length} of ${n - 1} merges joining ${plural(n, noun)}. The last three merges are at heights ${sentenceList(tops)}. A cut at ${fmt(cutHeight)}, in the gap between ${fmt(heightBelow)} and ${fmt(heightAbove)}, gives ${k} clusters of ${sentenceList(sizes)}.`}>
				<Axes axX={{ ticks: [] }} axY={axY} x={x} y={y} box={box} yLabel="Merge height (h): how costly the merge was" />
				{links.map((l, i) => (
					<g key={i} onMouseEnter={() => setHover({ cx: x(l.x), lines: [l.cluster == null ? "Merge above the cut" : `Merge inside ${clusterNames[l.cluster]}`, `Joins ${plural(l.size, noun)}`, `Height ${fmt(l.height)}`] })}
						onMouseLeave={() => setHover(null)}>
						<path d={path(l)} fill="none" stroke="transparent" strokeWidth="10" />
						<path d={path(l)} fill="none" stroke={clusterColor(l.cluster)} strokeWidth={l.cluster == null ? 1.5 : 2} />
					</g>
				))}
				{leaves.map((lf) => (
					<g key={lf.x} onMouseEnter={() => setHover({ cx: x(lf.x), lines: [`A branch of ${plural(lf.size, noun)}`, lf.size > 1 ? `Merged among themselves below height ${fmt(lf.height)}` : "Not merged with anyone yet"] })}
						onMouseLeave={() => setHover(null)}>
						<rect x={x(lf.x) - 9} y={box.b - 4} width="18" height="26" fill="transparent" />
						<circle cx={x(lf.x)} cy={y(0)} r="2.5" fill={clusterColor(lf.cluster)} />
						<text x={x(lf.x)} y={box.b + 16} fontSize="11" fill={MUTED} textAnchor="middle">{lf.size}</text>
					</g>
				))}
				{brackets.map((b) => (
					<g key={b.cluster} pointerEvents="none">
						<line x1={b.x1} x2={b.x2} y1={box.b + 25} y2={box.b + 25} stroke={clusterColor(b.cluster)} strokeWidth="2" />
						<text x={(b.x1 + b.x2) / 2} y={box.b + 41} fontSize="12" fill={INK} fontWeight="600" textAnchor="middle">{clusterNames[b.cluster]}</text>
						<text x={(b.x1 + b.x2) / 2} y={box.b + 55} fontSize="11" fill={MUTED} textAnchor="middle">{plural(b.size, noun)} at the cut</text>
					</g>
				))}
				<text x={box.r} y={box.b + 80} fontSize="11" fill={MUTED} textAnchor="end">Each leaf: a branch of earlier merges, labeled with its number of {noun}s (left-to-right order is arbitrary)</text>
				<line x1={box.l} x2={box.r} y1={y(cutHeight)} y2={y(cutHeight)} stroke={INK} strokeWidth="1.5" strokeDasharray="6 4" pointerEvents="none" />
				<Halo x={box.r} y={y(cutHeight) - 6} fill={INK} fontWeight="600" textAnchor="end">Cut: {k} clusters</Halo>
				{hover && <Tooltip x={hover.cx} lines={hover.lines} />}
			</svg>
			<figcaption className="mt-1 text-xs text-gray-600">
				Read the tree from the bottom up: each join is a merge, drawn at its height (how much it added to the spread within clusters). Only the last {links.length} of {n - 1} merges are drawn.
				{" "}The long stretch with no merges, from {fmt(heightBelow)} to {fmt(heightAbove)}, suggests cutting there, which leaves {k} clusters of {sentenceList(sizes)} {noun}s.
				{moved > 0 ? ` k-means then moved ${plural(moved, noun)} between them, for final sizes of ${sentenceList(finalSizes.map(count))}; each branch is colored by the cluster most of its ${noun}s ended in.` : ""}
			</figcaption>
		</figure>
	);
}
